import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import LoadingSpinner from "../LoadingSpinner";
import EmptyBanner from "../EmptyBanner";
import {useIsMutating, useQueryClient} from '@tanstack/react-query';

import Meeting, {cancelMeetingForTalentPosition, confirmCompleteMeetingForTalentPosition} from "../../models/Meeting";
import ErrorBanner from "../ErrorBanner";
import CandidateMeetingRow from "./components/CandidateMeetingRow";
import {useAuth} from "../../providers/AuthProvider";
import {Table} from "reactstrap";
import MeetingStatusFilter from "./components/MeetingStatusFilter";
import ProposeInterviewModal from "./ProposeMeetingModal";
import {useProject} from "../../providers/ClientProjectProvider";
import TalentCandidate from "../../models/TalentCandidate";
import {useAPI} from "../../config/API";
import {Modal} from "antd";

const ManageCandidateMeetings = ({position, candidate, ...props}) => {

    const {auth} = useAuth();
    const {project} = useProject();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false)


    const isSavingMeeting = useIsMutating({mutationKey: ['requestOrUpdateInterviewMutation']});

    const [selectedStatuses, setSelectedStatuses] = useState(TB.filter([...laravel.meeting_statuses], status => status !== 'Past'));
    const {
        data: meetings,
        isFetching,
        error,
        refetch
    } = useAPI(['fetchMeetings', {projectId: project?.id, positionId: position?.id, candidateId: candidate?.id}], {
            enabled: TB.isDefined(candidate) && candidate.shortlisted?.(),

            onSuccess: (data) => {
                if (!!data) {
                    let hasProposedMeetingByAuth = TB.filter(data || [], meeting => meeting.status === 'Proposed' && meeting.creator.id === auth?.id).length;

                    let update = {
                        ...candidate,
                        upcoming_meetings_count: TB.filter(data, {status: 'Upcoming'}).length,
                        proposed_meetings_count: TB.filter(data, {status: 'Proposed'}).length
                    };

                    if (hasProposedMeetingByAuth !== candidate.job_candidate?.has_proposed_interview) {
                        update.has_proposed_interview = hasProposedMeetingByAuth;
                    }

                    if(position) {
                        return position.resetCandidateQueryDataForTalent(TalentCandidate.make(update))
                    }
                }
            }
        }
    );

    const handleConfirmCompletionMeeting = async (meetingId) => {
        setIsLoading(true);

        const res = await confirmCompleteMeetingForTalentPosition(position?.id, meetingId).catch(err => {
            console.log(err, 'confirmCompleteMeetingForTalentPosition error')
            setIsLoading(false);
            return TB.showServerErrorToast();
        });

        if (res) {
            TB.toast("Interview Marked Complete", {type: 'success'});
            await queryClient.invalidateQueries({
                predicate: ({queryKey}) => {
                    const params = queryKey[1] || {};

                    return queryKey[0] === 'fetchMeetings' &&
                        params.positionId === position?.id &&
                        params.projectId === project?.id
                }
            })
        }

        setIsLoading(false);
    }

    const handleCancelMeeting = async (meetingId, reasonId) => {
        setIsLoading(true);

        const res = await cancelMeetingForTalentPosition(position?.id, meetingId, reasonId).catch(err => {
            setIsLoading(false);
            return TB.showServerErrorToast();
        });

        if (res) {
            TB.toast("Interview successfully Canceled", {type: 'success'});

            await queryClient.invalidateQueries({
                predicate: ({queryKey}) => {
                    const params = queryKey[1] || {};

                    return queryKey[0] === 'fetchMeetings' &&
                        params.positionId === position?.id &&
                        params.projectId === project?.id
                }
            })
            await queryClient.invalidateQueries({
                predicate: ({queryKey}) => {
                    const params = queryKey[1] || {};

                    return queryKey[0] === 'getConversation' &&
                        params.positionId === position?.id &&
                        params.projectId === project?.id
                }
            })
        }

        setIsLoading(false);
    }

    if (project.hasRequestedSOW() && !candidate.job_candidate?.is_assigned) {
        return (
            <div className="p-3 lg:p-5">
                <div className="flex-center-column" style={{gap: 16}}>

                    <img src="/images/feedback-banner/search.png" alt="Unavailable" className="img-fluid" style={{width: 200}} />

                    <div className="text-center">
                        <p>
                            Interviews are disabled once SOW is in progress.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    if (candidate && !candidate.isShortlisted()) {
        return (
            <div className="p-3 lg:p-5">
                <div className="flex-center-column" style={{gap: 16}}>

                    <img src="/images/feedback-banner/search.png" alt="Unavailable" className="img-fluid" style={{width: 200}} />

                    <div className="text-center">
                        <p>
                            Candidate must be invited to shortlist and show interest for this role before interviews can be requested.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    if (!auth.canRequestTalentInterviewForProject(project)) {
        return (
            <div className="p-3 lg:p-5">
                <div className="flex-center-column" style={{gap: 16}}>

                    <img src="/images/feedback-banner/lock.png" alt="Missing Permission" className="img-fluid" style={{width: 200}} />

                    <div className="text-center">
                        <p>
                            You don't have the appropriate group permissions to manage interviews.
                            Please contact your administrator or Wripple client lead for more information.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    const visibleMeetings = useMemo(() => {
        if (!meetings?.length) {
            return [];
        }
        if (!selectedStatuses?.length) {
            return meetings;
        } else {
            return TB.filter(meetings, meeting => TB.includes(selectedStatuses, meeting.status))
        }
    }, [selectedStatuses?.length, meetings]);

    return (
        <div id="interviews-table-popup-boundary"
             className="proposal-padding flex flex-col items-stretch flex-1 h-full">
            <div className="flex flex-row items-center" style={{gap: 16}}>
                <h3 className="proposal-tab-header">Interviews</h3>

                <button
                    className="btn btn-outline-primary relative"
                    disabled={isFetching || candidate.job_candidate?.has_proposed_interview || !candidate.job_candidate?.can_request_interview}
                    onClick={() => {
                        return setIsScheduleMeetingOpen(true)
                    }}
                >
                    Request Interview

                    {!candidate.job_candidate?.has_proposed_interview && !isFetching && !meetings?.length && !error && (
                        <span className="pulse-indicator">
                            <span className="blob info"></span>
                        </span>
                    )}

                </button>

                {!!meetings?.length && !error && selectedStatuses && (
                    <MeetingStatusFilter
                        meetings={meetings}
                        selectedStatuses={selectedStatuses}
                        onChange={setSelectedStatuses}/>
                )}

            </div>


            {(!isFetching && error) && (
                <ErrorBanner title="Something went wrong" message="Failed to load interviews"
                             onRetry={refetch}/>
            )}

            {(!isFetching && !meetings?.length && !error) && (
                <EmptyBanner message={"No Interviews Yet"}/>
            )}

            {(!!meetings?.length && !error) && (

                <React.Fragment>

                    <Table
                        hover
                        responsive="h-full"
                        className="interviews-table"
                    >
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Who</th>
                            <th>When</th>
                            <th>Status</th>
                            <th></th>
                        </tr>

                        </thead>
                        <tbody>
                        {!!visibleMeetings?.length ? visibleMeetings.map(meeting => (
                            <CandidateMeetingRow
                                key={`meeting-${meeting.id}`}
                                meeting={meeting}
                                candidate={candidate}
                                position={position}
                                onUpdate={props => {
                                    let newMeeting = Meeting.make({
                                        ...meeting, ...props
                                    });
                                    queryClient.setQueryData(['fetchMeetings', {
                                        positionId: position?.id,
                                        projectId: project?.id,
                                        candidateId: candidate?.id
                                    }], data => {
                                        if (!data) return undefined;

                                        return TB.map(data, item => item.id === newMeeting?.id ? newMeeting : item)
                                    })
                                }}
                                canRequestInterview={candidate.job_candidate?.can_request_interview}
                                hasProposedInterviews={candidate.job_candidate?.has_proposed_interview}
                                onConfirmComplete={handleConfirmCompletionMeeting}
                                onCancel={handleCancelMeeting}
                            />
                        )) : (
                            <tr>
                                <td colSpan={5}>
                                    <p className="mb-0 p-1 text-center">
                                        {!!meetings.length ? "No interviews found. Update or change the selected filters" : "No interviews requested yet."}
                                    </p>
                                </td>
                            </tr>
                        )}

                        </tbody>
                    </Table>
                </React.Fragment>
            )}
            <Modal
                okText={null}
                zIndex={199}
                footer={null}
                cancelText={null}
                title={null}
                className="schedule-meeting-modal"
                open={isScheduleMeetingOpen}
                closeable={!isSavingMeeting?.length}
                maskClosable={!isSavingMeeting?.length}
                style={{top: 20}}
                destroyOnClose={true}
                width={800}
                onCancel={() => setIsScheduleMeetingOpen(false)}
            >

                {isScheduleMeetingOpen && (
                    <ProposeInterviewModal
                        project={project}
                        position={position}
                        candidate={candidate}
                        onClose={() => setIsScheduleMeetingOpen(false)}/>
                )}


            </Modal>

            {(isFetching || isLoading) && (
                <div className="w-full flex justify-center absolute"
                     style={{bottom: 0, top: 0, zIndex: 2, paddingTop: 80, background: 'rgba(255,255,255,0.5)'}}>
                    <LoadingSpinner/>
                </div>
            )}
        </div>
    );
};

ManageCandidateMeetings.propTypes = {
    position: PropTypes.any,
    candidate: PropTypes.any,
    opportunity: PropTypes.any,
    disabled: PropTypes.any
}

export default ManageCandidateMeetings